import React from 'react';
import { useNavigate } from 'react-router-dom';

import { connect } from 'react-redux';
import {
  fetchDefaultValues, selectDefaultValues, selectInputFormLoading,
  selectInputFormError,
} from '../../redux/inputFormSlice';
import * as dbConstants from '../../dao/dbConstants';
import {
  clearEntityOptions, fetchEntityOptions,
  selectOptionsLoading
} from '../../redux/optionsSlice';
import { getQuestionAnswers } from '../../redux/questionsSlice';

import InputControl from './InputControl';
import InputFormSkeleton from './InputFormSkeleton';
import LinkControl from '../LinkControl';
import QuestionAnswersUI from '../questions/QuestionAnswersUI';
import InputSearch from '../inputs/InputSearch';
import Button from '../Button';

import PropTypes from 'prop-types';
import { saveRecord, getRecord, uploadFileInS3 } from '../../utils/apiUtils';
import { getFileFields, getInputConfig, removeHiddenAttributes, validateRecord } from './inputUtils';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import { evaluateCondition, objectsAreDifferent } from '../../utils/utils';
import {
  INPUT_LAYOUT_TYPE_SEPARATE_LABEL, INPUT_TYPE_BUTTON, INPUT_TYPE_CUSTOM_COMPONENT,
  INPUT_TYPE_FILE, INPUT_TYPE_LINK, INPUT_TYPE_NO_INPUT, INPUT_TYPE_QUESTIONS,
  INPUT_TYPE_READ_ONLY,
  INPUT_TYPE_SEARCH_FIELD, INPUT_TYPE_SELECT_SINGLE
} from '../componentConstants';
import './InputForm.css';

class InputForm extends React.Component {
  constructor(props) {
    super(props);
    this.cacheKey = props.cacheKey ?? new Date().getTime();
    this.previousOptionFuncInputsRef = {};
    this.previousFormFieldValuesRef = {};
    this.state = {
      record: props.initialRecord ?? {},
      loading: props.loading === true,
      saving: props.saving === true,
      errors: props.errors ?? {},
      inputConfig: props.inputConfig ?? getInputConfig(props.entityName)
    };
    this.dispatch = props.dispatch;
    this.navigate = props.navigate;
    this.fetchOptions = this.fetchOptions.bind(this);
    this.clearOptions = this.clearOptions.bind(this);
    this.onParentFieldChange = this.onParentFieldChange.bind(this);
    this.initializeData = this.initializeData.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.getInputByFieldName = this.getInputByFieldName.bind(this);
    this.onInputFocus = this.onInputFocus.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
    this.uploadFile = this.uploadFile.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onCancelInternal = this.onCancelInternal.bind(this);
    this.clearOptionsCache = this.clearOptionsCache.bind(this);
    this.isVisible = this.isVisible.bind(this);
    this.renderInputSection = this.renderInputSection.bind(this);
    this.changeReaction = this.changeReaction.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  handleKeyDown(e) {
    if (e.key === 'Enter') {
      const target = e.target;
      if (target.tagName.toLowerCase() === 'input') {
        e.preventDefault();
      }
    }
  }

  componentDidMount() {
    const { id, initialRecord, entityName, defaultValues, dispatch, initialValues } = this.props;
    if (initialRecord?.id) {
      this.initializeData(initialRecord);
      this.setState({ record: initialRecord });
    }
    else if (id) {
      getRecord(entityName, id)
        .then((fetchedRecord) => {
          if (!fetchedRecord || Object.keys(fetchedRecord).length === 0) {
            console.error('InputForm: Failed to fetch record - empty or unauthorized');
            return;
          }
          if (entityName === dbConstants.ENTITY_NAME_ARTICLE && fetchedRecord?.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR) {
            console.log('Fetched TripAdvisor article:', {
              id: fetchedRecord.id,
              metadata: fetchedRecord.metadata,
              locationId: fetchedRecord.locationId,
              source: fetchedRecord.source
            });
          }
          this.initializeData(fetchedRecord);
          this.setState({ record: fetchedRecord });
        })
        .catch((error) => {
          console.error('InputForm: Error fetching record:', error);
        });
    }
    else if (!defaultValues) {
      dispatch(fetchDefaultValues(entityName));
    }
    else if (defaultValues) {
      this.initializeData(defaultValues);
      this.setState({ record: { ...defaultValues, ...initialValues } });
    }
  }

  componentDidUpdate(prevProps) {
    if (!!prevProps.inputConfig && objectsAreDifferent(prevProps.inputConfig, this.props.inputConfig)) {
      this.setState(prevState => ({ ...prevState, inputConfig: this.props.inputConfig }), () => this.componentDidUpdate2(prevProps));
    }
    else {
      this.componentDidUpdate2(prevProps);
    }
  }

  componentDidUpdate2(prevProps) {
    const { id, initialRecord, defaultValues, entityName, initialValues, errors, loading, saving } = this.props;
    const otherStateChanges = { errors: errors ?? {}, loading: loading === true, saving: saving === true };
    const prevId = prevProps.id ?? prevProps?.initialRecord?.id ?? prevProps?.defaultValues?.id;
    const newId = id ?? initialRecord?.id ?? defaultValues?.id;
    if (!prevProps.defaultValues && defaultValues && !id) {
      this.setState(prevState => ({ ...prevState, ...otherStateChanges, record: { ...defaultValues, ...initialValues } }), () => {
        this.initializeData(defaultValues);
      });
    }
    else if (prevProps.entityName !== entityName || prevId !== newId) {
      if (!prevId && !!newId) {
        
        if (initialRecord) {
          this.initializeData(initialRecord);
          this.setState(prevState => ({ ...prevState, ...otherStateChanges, record: initialRecord }));
        }
        else if (id) {
          this.setState(prevState => ({ ...prevState, ...otherStateChanges, loading: true }));
          
          getRecord(entityName, id)
            .then((fetchedRecord) => {
              if (!fetchedRecord || Object.keys(fetchedRecord).length === 0) {
                console.error('InputForm: Failed to fetch record on update');
                this.setState({ loading: false });
                return;
              }
              this.initializeData(fetchedRecord);
              this.setState({ record: fetchedRecord, loading: false });
            })
            .catch((error) => {
              console.error('InputForm: Error fetching record on update:', error);
              this.setState({ loading: false });
            });
        }
        else {
          const nextRecord = defaultValues ?? {};
          this.initializeData(nextRecord);
          this.setState(prevState => ({ ...prevState, ...otherStateChanges, record: nextRecord }));
        }
      }
      else {
        this.previousOptionFuncInputsRef = {};
        if (id && !initialRecord) {
           this.setState(prevState => ({ ...prevState, ...otherStateChanges, loading: true }));
           getRecord(entityName, id)
            .then((fetchedRecord) => {
              if (!fetchedRecord) return;
              this.initializeData(fetchedRecord);
              this.setState({ record: fetchedRecord, loading: false });
            })
            .catch((err) => {
              console.error(err);
              this.setState({ loading: false });
            });
        } else {
           this.setState(prevState => ({ ...prevState, ...otherStateChanges }));
        }
      }
    }
    else if (!!initialValues && objectsAreDifferent(prevProps.initialValues, this.props.initialValues)) {
      const newRecordValues = { ...this.state.record };
      let needsUpdate = false;
      Object.keys(initialValues).forEach(key => {
        if (!prevProps.initialValues || (initialValues[key] !== this.state.record[key] && initialValues[key] !== prevProps.initialValues[key])) {
          newRecordValues[key] = initialValues[key];
          needsUpdate = true;
        }
      });

      if (needsUpdate) {
        this.setState(prevState => ({ ...prevState, ...otherStateChanges, record: newRecordValues }), () => {
          Object.keys(this.props.initialValues).forEach(key => {
            if (!prevProps.initialValues || prevProps.initialValues[key] !== this.props.initialValues[key]) {
              this.onParentFieldChange(key, this.props.initialValues[key], false);
            }
          });
        });
      }
    }
  }

  componentWillUnmount() {
    this.clearOptionsCache();
    this.previousOptionFuncInputsRef = {};
  }

  async fetchOptions(entityName, fieldName, func, input) {
    const strigifiedInput = JSON.stringify(input ?? {});
    if (strigifiedInput === this.previousOptionFuncInputsRef[entityName + '.' + fieldName]) {
      return;
    }
    this.previousOptionFuncInputsRef[entityName + '.' + fieldName] = strigifiedInput;
    return this.dispatch(fetchEntityOptions({
      entityName,
      fieldName,
      optionsFunc: func,
      optionsFuncInput: input,
      cacheKey: this.cacheKey
    }));
  }

  clearOptions(fullyQualifiedFieldName) {
    this.dispatch(clearEntityOptions({ fullyQualifiedFieldName, cacheKey: this.cacheKey }));
  }

  /**
   * Cascades a field's new value to the inputs that depend on it, then
   * validates it the way leaving the field would.
   *
   * `validate` is false when the caller is loading a record rather than
   * relaying something the user did: validating then marks every empty
   * required field as an error on arrival, which disables the submit button
   * before the form has been touched. On a first visit the errors happened to
   * be cleared again by the update that brought the default values in; once
   * those defaults were cached in the store, nothing cleared them and a New
   * screen opened unusable until each required field was typed into.
   * Submitting still validates the whole record, so nothing is let through.
   */
  async onParentFieldChange(fieldName, newValue, validate = true) {
    const { inputConfig } = this.state;
    if (inputConfig) {
      const newRecord = {};
      const promises = [];
      if (this.previousFormFieldValuesRef[fieldName] === newValue) {
        return;
      }
      inputConfig.sections.forEach((section) => {
        section.inputs.forEach((input) => {
          if (input.dependsOn && input.dependsOn === fieldName) {
            if (input?.inputType === INPUT_TYPE_SELECT_SINGLE && !!input.optionsFunc) {
              newRecord[input.fieldName] = null;
              if (newValue === '' || newValue === null || newValue === undefined) {
                input.optionsFuncInput[fieldName] = '';
              }
              else if (input.optionsEntityName) {
                this.previousFormFieldValuesRef[fieldName] = newValue;
                input.optionsFuncInput[fieldName] = newValue;
                const promise = this.fetchOptions(input.optionsEntityName, input.fieldName, input.optionsFunc, input.optionsFuncInput);
                promises.push(promise)
              }
            }
          }
        });
      });
      if (Object.keys(newRecord).length > 0) {
        await new Promise(resolve => {
          this.setState((prevState) => ({ ...prevState, record: { ...prevState.record, ...newRecord, [fieldName]: newValue } }), resolve);
        });
      }
      if (promises.length) {
        await Promise.all(promises);
      }

      inputConfig.sections.forEach((section) => {
        section.inputs.forEach((input) => {
          if (input.inputType === INPUT_TYPE_QUESTIONS && input.dependsOn === fieldName && newValue) {
            const recordId = this.state.record.id || this.props.id;
            this.props.getQuestionAnswers(
              this.props.entityName,
              newValue, // typeId (productTypeId for Product and companyTypeId for others)
              recordId,
              this.cacheKey
            );
          }
        });
      });
      if (validate) {
        this.onInputBlur(fieldName, newValue);
      }
    }
  }

  async initializeData(record) {
    const { inputConfig } = this.state;
    let hasChangeReaction = false;
    inputConfig.sections.forEach((section) => {
      section.inputs.forEach((input) => {
        hasChangeReaction = hasChangeReaction || (!!input.changeReaction?.fields && !!Object.keys(input.changeReaction.fields)?.length);
        if (input.optionsEntityName && input.optionsFunc) {
          let funcInput = input.optionsFuncInput ?? {};
          if (input.dependsOn) {
            input.optionsFuncInput[input.dependsOn] = record[input.dependsOn] ?? '';
          }
          input.optionsFuncInput = funcInput;
          this.fetchOptions(input.optionsEntityName, input.fieldName, input.optionsFunc, input.optionsFuncInput);
        }
        else if (!!input.dependsOn && !!record[input.dependsOn]) {
          hasChangeReaction = true;
        }
        else if (input.inputType === INPUT_TYPE_QUESTIONS && !input.dependsOn) {
          const recordId = record.id || this.props.id;
          const typeId = record[input?.subTypeIdAttributeName] || 0;

          this.props.getQuestionAnswers(
            this.props.entityName,
            typeId,
            recordId,
            this.cacheKey
          );
        }
      });
    });
    if (hasChangeReaction) {
      Object.keys(record).forEach((key) => {
        this.onParentFieldChange(key, record[key], false);
      });
    }
  }

  async changeReaction(fieldName, newValue) {
    if (!newValue) return;
    const input = this.getInputByFieldName(fieldName);
    if (!input || !input.changeReaction || !Object.keys(input.changeReaction)?.length) return;
    const changeReaction = input.changeReaction;

    const newRecord = await getRecord(changeReaction.entityName, newValue);

    const updatedFields = {};
    const parentChangePromises = Object.keys(changeReaction.fields).map((key) => {
      const dependentFieldName = changeReaction.fields[key];
      const dependentFieldValue = newRecord[key];
      updatedFields[dependentFieldName] = dependentFieldValue;
      return this.onParentFieldChange(dependentFieldName, dependentFieldValue);
    });

    await Promise.all(parentChangePromises);

    // Waited so that the props.onChange is called after the state is updated
    await new Promise(resolve => {
      this.setState((prevState) => ({ ...prevState, record: { ...prevState.record, ...updatedFields } }), resolve);
    });
  }

  async onInputChange(input, newValue) {
    const fieldName = input.fieldName;
    if (input.inputType === INPUT_TYPE_FILE && !!newValue?.length) newValue = newValue[0];

    await new Promise(resolve => {
      this.setState((prevState) => ({ ...prevState, record: { ...prevState.record, [fieldName]: newValue } }), resolve);
    });

    await this.onParentFieldChange(fieldName, newValue);
    await this.changeReaction(fieldName, newValue);

    if (this.props.onChange) {
      this.props.onChange(this.state.record);
    }

    if (fieldName === 'articleTypeId') {
      this.setState({ errors: {} });
    } else {
      this.onInputBlur(fieldName, newValue);
    }
  }

  getInputByFieldName(fieldName) {
    let input = null;
    this.state.inputConfig.sections.forEach((section) => {
      section.inputs.forEach((inputObj) => {
        if (inputObj.fieldName === fieldName) {
          input = inputObj;
        }
      });
    });
    return input;
  }

  async onInputFocus(fieldName, value) {
    if (value && String(value).trim() !== '') {
      this.onInputBlur(fieldName, value);
    }
  }

  async onInputBlur(fieldName, value) {
    const updatedRecord = { ...this.state.record, [fieldName]: value };

    const validationErrors = validateRecord(this.state.inputConfig, updatedRecord);

    const fieldError = validationErrors[fieldName];

    const isTripAdvisorLocation =
      this.props.entityName === dbConstants.ENTITY_NAME_ARTICLE &&
      updatedRecord.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR &&
      fieldName === 'locationId';

    if (isTripAdvisorLocation) {
      console.log('Validating TripAdvisor locationId:', {
        hasValue: !!value,
        validationResult: !!fieldError ? 'error' : 'valid'
      });
    }

    if (!!fieldError) {
      this.setState((prevState) => ({ ...prevState, errors: { ...prevState.errors, [fieldName]: fieldError } }));
    }
    else {
      const newErrorsObj = { ...this.state.errors };

      if (newErrorsObj[fieldName]) {
        console.log(`Clearing error for field: ${fieldName}`);
        delete newErrorsObj[fieldName];
        this.setState({ errors: newErrorsObj });
      }
    }
  }

  async uploadFile(fieldName, fileNameField, record) {
    let file = record[fieldName];
    delete record[fieldName];
    delete record[fileNameField];
    const fileUploadResponse = await uploadFileInS3(this.props.entityName, file, record);
    return fileUploadResponse;
  }

  async onSubmit(e) {
    e.preventDefault();
    const { beforeSave, doNotSave, entityName, validateInput, initialRecord } = this.props;
    if (!!beforeSave && doNotSave === true && validateInput === false) {
      beforeSave(entityName, { ...this.state.record });
      return;
    }

    const { inputConfig } = this.state;

    const originalRecord = initialRecord || this.props.defaultValues || {};
    const hasChanges = objectsAreDifferent(originalRecord, this.state.record);
    
    if (!hasChanges && !!this.state.record.id) {
      console.log('InputForm.onSubmit - No changes detected, skipping save');
      if (doNotSave && !!beforeSave) {
        beforeSave(entityName, this.state.record);
      }
      else if (!!this.props.afterSave) {
        this.props.afterSave(this.props.entityName, this.state.record);
      }
      else if (!!this.props.backRoute) {
        this.navigate(this.props.backRoute);
      }
      return;
    }

    this.setState({ saving: true, errors: {} });

    const record = this.state.record;
    if (
      this.props.entityName === dbConstants.ENTITY_NAME_ARTICLE &&
      record.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR
    ) {
      if (!record.locationId) {
        const errors = { ...this.state.errors };
        errors.locationId = {
          shortMessage: 'Selection required',
          fullMessage: 'You must select a TripAdvisor location before saving'
        };

        this.setState({ errors, saving: false });

        showErrorMessage("You must select a TripAdvisor location from the search results");
        return;
      }
    }

    let recordCopy = { ...this.state.record };
    try {
      // Validate the form using our validateForm method
      // This will update the errors state internally
      const isValid = this.validateForm();

      if (!isValid) {
        setTimeout(() => { // because state changes might not have been available as they were set in validateForm function.
          const errorMessage = Object.values(this.state.errors).map(error => '- ' + error.fullMessage).join('\n');
          showErrorMessage(errorMessage);
          this.setState({ saving: false });
        }, 20);
        return;
      }

      recordCopy = removeHiddenAttributes(this.props.entityName, inputConfig, recordCopy);
      const fileFields = getFileFields(this.props.entityName, inputConfig, recordCopy);
      if (!!fileFields?.length) {
        for (const fileField of fileFields) {
          if (!!recordCopy[fileField.fieldName]) {
            if (fileField.ignore) {
              delete recordCopy[fileField.fieldName];
              this.setState(prevState => {
                const record1 = prevState.record;
                delete record1[fileField.fieldName];
                return { ...prevState, record: { ...record1 } };
              })
            }
            else {
              const response = await this.uploadFile(fileField.fieldName, fileField.fileNameField, recordCopy);
              this.setState((prevState) => ({
                ...prevState,
                record: {
                  ...prevState.record,
                  [fileField.fileNameField]: response.fullFileName,
                  id: response.id,
                  updatedOn: response.updatedOn ? response.updatedOn : prevState.updatedOn,
                  createdOn: response.createdOn ? response.createdOn : prevState.createdOn,
                  createdById: response.createdById ? response.createdById : prevState.createdById,
                },
              }));
              recordCopy[fileField.fileNameField] = response.fullFileName;
              recordCopy.id = response.id;
              if (!!response.updatedOn) recordCopy.updatedOn = response.updatedOn;
              if (!!response.createdOn) recordCopy.createdOn = response.createdOn;
              if (!!response.createdById) recordCopy.createdById = response.createdById;
            }
          }
        }
      }
    }
    catch (err) {
      this.setState({ saving: false });
      console.error('InputForm.onSubmit - error', err);
      const message = !!err?.response?.data?.isForUser === true ? err.response.data.message : err.message;
      showErrorMessage(message);
      return;
    }

    const isTripAdvisorArticle =
      this.props.entityName === dbConstants.ENTITY_NAME_ARTICLE &&
      recordCopy.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR &&
      recordCopy.locationId;

    if (isTripAdvisorArticle) {
      console.log('Saving TripAdvisor article with locationId:', recordCopy.locationId);
      showSuccessMessage('Saving Tripadvisor location. This might take a moment while we process the content...');
    }

    if (!!this.props.beforeSave) {
      this.props.beforeSave(this.props.entityName, this.state.record, this.state.errors);
    }

    if (this.props.doNotSave === true) {
      console.log('InputForm.onSubmit - doNotSave is true, not saving record:', recordCopy);
      this.setState(prevState => ({ ...prevState, saving: false }));
      return;
    }

    let savedRecord = null;
    try {
      savedRecord = await saveRecord(this.props.entityName, recordCopy);

      // IMPORTANT: Update the state with the COMPLETE saved record, not just merge
      // This ensures updatedOn and other timestamp fields are properly updated
      const updatedRecord = { ...this.state.record, ...savedRecord };
      this.setState(prevState => ({ ...prevState, record: updatedRecord }));

      const displayName = savedRecord?._displayName || this.props.entityName;
      if (isTripAdvisorArticle) {
        showSuccessMessage(`${displayName} saved successfully with content indexed for search!`);
      } else {
        showSuccessMessage(`${displayName} saved successfully`);
      }
    }
    catch (error) {
      console.error('InputForm.onSubmit - error', error);
      let message = `Saving ${this.props.entityName} failed. ${error.message}`
      if (error?.response?.data?.isForUser === true) {
        message = error.response.data.message;
      }
      showErrorMessage(message);
      return;
    }
    finally {
      this.setState(prevState => ({ ...prevState, saving: false }));
    }

    if (!!this.props.afterSave) {
      const finalRecord = { ...this.state.record, ...savedRecord };
      this.props.afterSave(this.props.entityName, finalRecord);
    }
    else if (!!this.props.backRoute) {
      this.navigate(this.props.backRoute);
    }
  }

  onCancelInternal() {
    this.clearOptionsCache();
    if (!!this.props.onCancel) {
      this.props.onCancel();
    }
    if (!!this.props.backRoute) {
      this.navigate(this.props.backRoute);
    }
  }

  clearOptionsCache() {
    Object.keys(this.previousOptionFuncInputsRef).forEach((fullyQualifiedFieldName) => {
      this.clearOptions(fullyQualifiedFieldName);
    });
  }

  isVisible(input, record) {
    if (!!input.isHidden) {
      return !(evaluateCondition(input.isHidden, record))
    }
    const result = !input?.dependsOn || !!record[input.dependsOn] || !input.showWithDependency;
    return result;
  }

  validateForm() {
    const validationErrors = validateRecord(this.state.inputConfig, this.state.record);

    const isTripAdvisorArticle =
      this.props.entityName === dbConstants.ENTITY_NAME_ARTICLE &&
      this.state.record.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR;

    if (isTripAdvisorArticle) {
      if (!this.state.record.locationId) {
        validationErrors.locationId = {
          shortMessage: 'Location is required',
          fullMessage: 'You must select a location from the search results for TripAdvisor articles'
        };
      } else {
        delete validationErrors.locationId;

        const newErrors = { ...this.state.errors };
        if (newErrors.locationId) {
          delete newErrors.locationId;
          this.setState({ errors: newErrors });
        }
      }
    }

    this.setState({ errors: validationErrors });

    return Object.keys(validationErrors).length === 0;
  }

  renderInputSection(section, sectionIndex) {
    const { record, errors, loading } = this.state;
    const { loadingDefaultValues } = this.props;

    return (
      <div key={section.heading || 'section' + sectionIndex} className="section">
        {section.heading && !!section.inputs?.length && section.inputs[0].inputType !== INPUT_TYPE_QUESTIONS && <h3 className="heading">{section.heading}</h3>}
        {
          section.inputs.map((input, inputIndex) => {
            if (!this.isVisible(input, record)) return null;

            const isDisabled = evaluateCondition(input.isDisabled, record) || loadingDefaultValues || loading;

            if (input.inputType === INPUT_TYPE_LINK) {
              return <LinkControl key={sectionIndex + '_' + inputIndex} link={input} record={record} />;
            }
            if (input.inputType === INPUT_TYPE_BUTTON) {
              return <div className='buttons'>
                <button key={sectionIndex + '_' + inputIndex} type={input.type ?? 'button'} className={input?.className ?? ''} onClick={input.onClick}>
                  {!!input.isLoading && <i className="bx bx-loader-alt bx-spin bx-gap" />}
                  {input.label}
                </button>
              </div>;
            }
            if (input.inputType === INPUT_TYPE_CUSTOM_COMPONENT) {
              const ReactComponentName = input.componentRef;

              const customProps = {
                ...input.componentInput,
                key: `${sectionIndex}_${inputIndex}_${input.fieldName}`,
                cacheKey: input.componentInput?.cacheKey ?? this.cacheKey
              };

              if (input.changeHandlerPropName) {
                customProps.value = record[input.fieldName];
                customProps[input.changeHandlerPropName] = (newValue) => this.onInputChange(input, newValue);
              }

              return (React.createElement(ReactComponentName, customProps));
            }
            else if (input.inputType === INPUT_TYPE_SEARCH_FIELD) {
              return <InputSearch key={sectionIndex + '_' + inputIndex}
                onChange={(e) => this.onInputChange(input, e.target.value)}
                onSearch={input.onSearch}
                value={record[input.fieldName] || input.initialValue || ''}
                placeholder={input.placeholder}
                isDisabled={isDisabled}
                error={errors[input.fieldName]}
                onFocus={() => this.onInputFocus(input.fieldName, record[input.fieldName])}
                onBlur={() => this.onInputBlur(input.fieldName, record[input.fieldName])}
              />
            }
            // FIXME - Remove the following block and use INPUT_TYPE_CUSTOM_COMPONENT to render this component.
            else if (input.inputType === INPUT_TYPE_QUESTIONS) {
              return (
                <QuestionAnswersUI key={sectionIndex + '_' + inputIndex}
                  cacheKey={this.cacheKey}
                  entityName={this.props.entityName}
                  fieldName={input.fieldName}
                  record={record}
                  errors={errors}
                  onChange={(value) => this.onInputChange(input, value)}
                  isDisabled={isDisabled}
                  typeId={record[input?.subTypeIdAttributeName] ?? record.companyTypeId ?? 0}
                  recordId={record.id || this.props.id}
                  layoutType={input.layoutType ?? INPUT_LAYOUT_TYPE_SEPARATE_LABEL}
                  heading={section.heading ?? ''}
                />
              );
            }
            else if (input.inputType === INPUT_TYPE_NO_INPUT) {
              return (
                <div key={sectionIndex + '_' + inputIndex} style={input.style ?? {}}>{input.text}</div>
              );
            }
            else if (input.inputType === INPUT_TYPE_READ_ONLY) {
              return (
                !evaluateCondition(input.isHidden, record) && (
                  <div className={`input-control read-only ${input.layoutType === INPUT_LAYOUT_TYPE_SEPARATE_LABEL ? 'separate-label' : 'inline'
                    }`}
                  >
                    <label>
                      {input.label}
                      {!!input.label && (input?.validationRules?.isRequired || evaluateCondition(input.isRequired, record)) ? (
                        <span className="required">*</span>
                      ) : null}
                    </label>
                    {!!input.formatValue ? input.formatValue(record) : record[input.fieldName]}
                  </div>
                )
              );
            }
            else {
              return (
                <InputControl
                  key={sectionIndex + '_' + inputIndex}
                  input={{ ...input, isDisabled: isDisabled }}
                  cacheKey={this.cacheKey}
                  value={record[input.fieldName]}
                  onChange={(value) => this.onInputChange(input, value)}
                  onBlur={this.onInputBlur}
                  placeholder={input.placeholder}
                  error={
                    // Special case: Hide source errors for TripAdvisor when a location is selected
                    input.fieldName === 'source' &&
                      record.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR &&
                      record.locationId ? null : errors[input.fieldName]
                  }
                  record={record}
                  formId={this.cacheKey}
                  forceRecordUpdate={(fieldName, value) => {
                    this.setState(prevState => ({
                      ...prevState,
                      record: {
                        ...prevState.record,
                        [fieldName]: value
                      }
                    }), () => {
                      setTimeout(() => this.validateForm(), 0);
                    });
                  }}
                  onFieldError={(fieldName, errorValue) => {
                    if (errorValue === null) {
                      if (fieldName === 'locationId' &&
                        this.props.entityName === dbConstants.ENTITY_NAME_ARTICLE &&
                        this.state.record.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR) {

                        setTimeout(() => {
                          this.validateForm();
                        }, 0);
                      }
                      else {
                        const newErrors = { ...this.state.errors };
                        delete newErrors[fieldName];
                        this.setState({ errors: newErrors });
                      }
                    }
                    else {
                      const newErrors = { ...this.state.errors };
                      newErrors[fieldName] = errorValue;
                      this.setState({ errors: newErrors });
                    }
                  }}
                />
              );
            }
          })
        }
      </div>
    );
  }

  render() {
    const { heading, id, loadingDefaultValues, errorLoadingDefaultValues,
      showButtons, otherButtonLabel, onOtherButtonClick, showCancelButton, isImageUploading, headerAction } = this.props;
    const { errors, inputConfig, loading, record, saving } = this.state;

    console.log('InputForm render - isImageUploading:', isImageUploading);

    if (loading || loadingDefaultValues) {
      return <InputFormSkeleton />;
    }

    if (errorLoadingDefaultValues) {
      return <div>Error: {errorLoadingDefaultValues}</div>;
    }

    if (!inputConfig) {
      return <div>Error: Input configuration not found.</div>;
    }

    const saveButtonLabel = !!this.props.saveButtonLabel
      ? saving ? this.props.saveButtonLabel + '...' : this.props.saveButtonLabel
      : !!id ? saving ? 'Updating...' : 'Update'
        : saving ? 'Saving...' : 'Save';

    return (
      <>
        {!!heading && (
          <div className="heading-row">
            <h2>{id ? 'Edit ' : 'New '}{heading}</h2>
            {}
            {headerAction && <div className="header-action">{headerAction}</div>}
          </div>
        )}
        <div className="client-area">
          <form className="record-form" onSubmit={this.onSubmit} onKeyDown={this.handleKeyDown} noValidate>
            <div className="record-form-fields-container">
              {inputConfig && inputConfig.sections.map(this.renderInputSection)}
            </div>
            {showButtons && <div className="buttons">
              {showCancelButton && (
                <Button
                  type="button"
                  className="plain"
                  disabled={saving}
                  onClick={this.onCancelInternal}
                >
                  Cancel
                </Button>
              )}
              {!!otherButtonLabel &&
                <Button type="button" className="secondary" disabled={saving} onClick={() => { if (!!onOtherButtonClick) onOtherButtonClick(record) }}
                >{otherButtonLabel}</Button>
              }
              <Button type="submit" className="primary submit"
                isAnimate={saving}
                disabled={!!Object.keys(errors)?.length || isImageUploading}
                title={
                  isImageUploading
                    ? 'Please wait while image is uploading...'
                    : !!Object.keys(errors)?.length
                      ? `Please fix validation errors: ${Object.keys(errors).join(', ')}`
                      : ''
                }
                onClick={() => {
                  if (Object.keys(errors)?.length) {
                    console.log('Save button clicked but disabled due to errors:', errors);
                  }
                  if (isImageUploading) {
                    console.log('Save button clicked but disabled due to image upload in progress');
                  }
                }}
                animationText={record?.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR && record?.locationId ? 'Processing Content...' : saveButtonLabel}
              >
                {saveButtonLabel}
              </Button>

            </div>}
          </form>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { inputConfig } = ownProps;
  const loadingStates = {};

  if (inputConfig) {
    inputConfig.sections.forEach(section => {
      section.inputs.forEach(input => {
        if (input.optionsEntityName && input.fieldName) {
          loadingStates[`${input.optionsEntityName}_${input.fieldName}_loading`] =
            selectOptionsLoading(state, input.optionsEntityName, input.fieldName);
        }
      });
    });
  }

  return {
    defaultValues: selectDefaultValues(state, ownProps.entityName),
    loadingDefaultValues: selectInputFormLoading(state),
    errorLoadingDefaultValues: selectInputFormError(state),
    ...loadingStates
  };
};

// Make getQuestionAnswers function available in the props so that it can be
// called when its required input fields' values change, to get the respective
// QuestionAnswers.
const mapDispatchToProps = (dispatch) => ({
  dispatch: dispatch,
  getQuestionAnswers: (entityName, typeId, recordId, cacheKey) =>
    dispatch(getQuestionAnswers(entityName, typeId ?? 0, recordId ?? 0, cacheKey)),
});

const WithNavigate = (props) => {
  const navigate = useNavigate();
  return <InputForm {...props} navigate={navigate} />;
};

InputForm.propTypes = {
  entityName: PropTypes.string.isRequired,
  initialRecord: PropTypes.object,
  id: PropTypes.number,
  defaultValues: PropTypes.object,
  initialValues: PropTypes.object,
  backRoute: PropTypes.string,
  onChange: PropTypes.func,
  onCancel: PropTypes.func,
  beforeSave: PropTypes.func,
  afterSave: PropTypes.func,
  doNotSave: PropTypes.bool,
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  validateInput: PropTypes.bool,
  showCancelButton: PropTypes.bool,
  isImageUploading: PropTypes.bool,
  headerAction: PropTypes.node,
};

InputForm.defaultProps = {
  showButtons: true,
  validateInput: true,
  loading: false,
  saving: false,
  doNotSave: false,
  showCancelButton: true,
};

export default connect(mapStateToProps, mapDispatchToProps)(WithNavigate);